/**
 * GTM tracking for <product-family-selector>.
 *
 * The selector owns all behaviour and calls this optional helper only for
 * shopper actions. Removing this asset disables tracking without changing the
 * selector itself.
 */
(() => {
const viewedSelectors = new Set();

class ProductFamilySelectorTracking {
  constructor(selector, model) {
    this.selector = selector;
    this.model = model;
    this.disabled = Boolean(window.Shopify?.designMode);
    this.startedAt = Date.now();
    this.interactionCount = 0;
    this.viewTracked = false;
  }

  compact(params) {
    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
    );
  }

  commonParams(controlType) {
    return this.compact({
      family_id: this.model.familyId,
      source_product_id: this.model.currentProductId,
      family_size: this.model.products.length,
      dimension_count: this.model.dimensionCount,
      layout_mode: this.model.layoutMode,
      control_type: controlType,
      interaction_count: this.interactionCount,
    });
  }

  push(event, params = {}) {
    if (this.disabled) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...this.commonParams(params.control_type), ...this.compact(params) });
  }

  trackView() {
    if (this.disabled || this.viewTracked) return;

    window.requestAnimationFrame(() => {
      if (this.viewTracked || !this.selector.isConnected || this.selector.hidden) return;

      const viewKey = `${this.model.familyId}|${this.model.currentProductId}`;
      if (viewedSelectors.has(viewKey)) return;

      this.viewTracked = true;
      viewedSelectors.add(viewKey);
      this.push('product_family_view');
    });
  }

  trackOpen() {
    this.push('product_family_open');
  }

  trackSelection({
    dimensionIndex,
    optionIndex,
    previousOptionIndex,
    removedOptionCount,
    targetProductId,
    controlType,
  }) {
    this.interactionCount += 1;
    const params = this.selectionParams({
      dimensionIndex,
      optionIndex,
      previousOptionIndex,
      removedOptionCount,
      targetProductId,
      controlType,
    });

    this.push('product_family_select', params);
    if (removedOptionCount > 0) this.push('product_family_adjustment', params);

    return params;
  }

  trackNavigate(params) {
    this.push('product_family_navigate', {
      ...params,
      time_to_product_seconds: Math.round((Date.now() - this.startedAt) / 100) / 10,
    });
  }

  selectionParams({
    dimensionIndex,
    optionIndex,
    previousOptionIndex,
    removedOptionCount,
    targetProductId,
    controlType,
  }) {
    const options = this.model.options[dimensionIndex] || [];
    return this.compact({
      dimension: this.model.dimensions[dimensionIndex]?.label,
      previous_value: previousOptionIndex == null ? undefined : options[previousOptionIndex],
      selected_value: options[optionIndex],
      had_conflict: removedOptionCount > 0,
      adjusted_dimension_count: removedOptionCount,
      target_product_id: targetProductId,
      control_type: controlType,
    });
  }
}

window.ProductFamilySelectorTracking = ProductFamilySelectorTracking;
})();
